/**
 * Préférences du joueur et écrans « hors partie » (DECISIONS §4.2, §6.9, §9.3).
 *
 * Règle tenue ici, sans exception : le **solde d'éclats** et le **niveau** d'une amélioration
 * affichés proviennent toujours d'une réponse serveur (`GET /me/upgrades`, `POST
 * /me/upgrades/:upgradeId` ou `GET /me`). Le client ne déduit ni ne décrémente jamais un montant :
 * il recopie la valeur reçue (DECISIONS §8, anti-triche).
 *
 * Les chemins des améliorations permanentes ne sont pas encore publiés par `API_PATHS` de
 * `@elemora/shared` (paquet hors périmètre de cette carte) : ils sont assemblés à partir de
 * `API_PATHS.me.root`, ce qui conserve un seul préfixe de ressource et évite une seconde vérité.
 */
import {
  collectionResponseSchema,
  meRunsResponseSchema,
  meUpgradesResponseSchema,
  updateMeResponseSchema,
  upgradeBuyResponseSchema,
  type CollectionResponse,
  type MeRunsResponse,
  type MeUpgradesResponse,
  type UpdateMeResponse,
  type UpgradeBuyResponse,
} from '@elemora/shared/api';
import {
  API_PATHS,
  RUNS_PAGE_LIMIT_DEFAUT,
  RUNS_PAGE_LIMIT_MAX,
  type Locale,
} from '@elemora/shared/constants';

import { requete } from './http';

/** `GET /me/upgrades` : catalogue complet des améliorations + état du joueur. */
const CHEMIN_AMELIORATIONS = `${API_PATHS.me.root}/upgrades`;

/** `POST /me/upgrades/:upgradeId` : chemin d'achat d'une entrée du catalogue. */
function cheminAchat(upgradeId: string): string {
  return `${CHEMIN_AMELIORATIONS}/${encodeURIComponent(upgradeId)}`;
}

/** Borne `limit` au contrat du serveur (1 à 50, défaut 20). */
export function bornerLimiteHistorique(limite: number): number {
  if (!Number.isFinite(limite)) return RUNS_PAGE_LIMIT_DEFAUT;
  return Math.min(Math.max(Math.trunc(limite), 1), RUNS_PAGE_LIMIT_MAX);
}

/**
 * `GET /me/upgrades` — catalogue complet et niveaux possédés.
 *
 * `eclats` est le solde persistant **d'autorité** : c'est cette valeur que l'écran des
 * améliorations affiche, jamais une valeur dérivée d'un achat précédent.
 */
export async function lireAmeliorations(): Promise<MeUpgradesResponse> {
  return requete({
    methode: 'GET',
    chemin: CHEMIN_AMELIORATIONS,
    schema: meUpgradesResponseSchema,
  });
}

/**
 * `POST /me/upgrades/:upgradeId` — achat du **niveau suivant**, débité par le serveur.
 *
 * `cleIdempotence` est fournie par l'appelant et doit rester **la même pendant toute la durée
 * d'une intention d'achat** (DECISIONS §4.1) : un double-clic ou une reprise après erreur réseau
 * ne doivent produire qu'un seul débit. Un nouvel achat décidé après la réponse du serveur est
 * une nouvelle intention, donc une nouvelle clé.
 *
 * Le corps est strictement vide : ni prix, ni niveau, ni montant (DECISIONS §6.9, §8.8).
 */
export async function acheterAmelioration(
  upgradeId: string,
  cleIdempotence: string,
): Promise<UpgradeBuyResponse> {
  return requete({
    methode: 'POST',
    chemin: cheminAchat(upgradeId),
    schema: upgradeBuyResponseSchema,
    corps: {},
    cleIdempotence,
  });
}

/**
 * `GET /me/collection` — le **grimoire** : toutes les cartes du contenu publié, possédées ou non
 * (arbitrage A-27).
 *
 * Le serveur décide de ce qui est révélé : une carte non possédée arrive sans `descKey`, sans
 * `effects` et sans `tags` (`null`), et `points`/`pointsByRarity` sont **recalculés côté serveur**
 * (barème de rareté). L'écran recopie ces valeurs, il ne les déduit jamais du contenu local.
 */
export async function lireCollection(): Promise<CollectionResponse> {
  return requete({
    methode: 'GET',
    chemin: API_PATHS.me.collection,
    schema: collectionResponseSchema,
  });
}

/**
 * `GET /me/runs?limit=n` — historique du **joueur connecté**.
 *
 * Aucun identifiant de joueur n'est transmis : le serveur le lit dans le jeton (DECISIONS §4.2).
 * Un client ne peut donc pas demander l'historique d'un autre compte.
 */
export async function lireHistorique(limite: number = RUNS_PAGE_LIMIT_DEFAUT): Promise<MeRunsResponse> {
  const borne = bornerLimiteHistorique(limite);
  return requete({
    methode: 'GET',
    chemin: `${API_PATHS.me.runs}?limit=${borne}`,
    schema: meRunsResponseSchema,
  });
}

/**
 * `PATCH /me {locale}` — la langue de l'interface est une préférence **serveur** (DECISIONS §9.3).
 *
 * Elle est donc retrouvée à la reconnexion, indépendamment du cookie local, qui ne sert que de
 * repli immédiat. Le corps est `.strict()` côté serveur : n'envoyer que `locale`.
 */
export async function enregistrerLocale(locale: Locale): Promise<UpdateMeResponse> {
  return requete({
    methode: 'PATCH',
    chemin: API_PATHS.me.root,
    schema: updateMeResponseSchema,
    corps: { locale },
  });
}
